import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  Input,
  Image,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import MainInput from "../../../../components/Form/input";
import MainModel from "../../../../components/Models/main-model";
import { closeAddSectionkModel } from "../../../../modules/admin-modules/models/Actions";
import { getAllGroupsRequest } from "../../../../modules/admin-modules/groups/Actions";
import {
  createAdminSectionRequest,
  editAdminSectionRequest,
} from "../../../../modules/admin-modules/sections/Actions";

const colourStyles = {
  control: (styles, state) => ({
    ...styles,
    width: "100%",
    backgroundColor: brandPalette.cream,
    color: "#1e1b17",
    borderRadius: "12px",
    borderColor: state.isFocused ? brandPalette.primary : brandPalette.stone,
    borderWidth: "1px",
    boxShadow: state.isFocused ? "0 0 0 1px rgba(91, 13, 22, 0.22)" : "none",
    minHeight: "42px",
    ":hover": { borderColor: brandPalette.primary },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (styles) => ({
    ...styles,
    color: brandPalette.stone,
    fontWeight: 600,
    fontSize: "14px",
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "rgba(91, 13, 22, 0.12)",
    borderRadius: "8px",
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: brandPalette.accentDark,
    fontWeight: 700,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: brandPalette.primary,
    ":hover": { backgroundColor: "rgba(91, 13, 22, 0.15)", color: brandPalette.accentDark },
  }),
  menu: (styles) => ({
    ...styles,
    borderRadius: "12px",
    overflow: "hidden",
    border: `1px solid ${brandPalette.stone}`,
    boxShadow: "0 16px 40px rgba(69, 11, 17, 0.18)",
    zIndex: 50,
  }),
  option: (styles, state) => ({
    ...styles,
    fontSize: "14px",
    fontWeight: state.isSelected ? 800 : 600,
    color: state.isSelected ? brandPalette.warmBeige : brandPalette.accentDark,
    backgroundColor: state.isSelected
      ? brandPalette.accentDark
      : state.isFocused
        ? "rgba(233, 226, 219, 0.7)"
        : "transparent",
    ":active": { backgroundColor: "rgba(216, 206, 195, 0.7)" },
  }),
};

const AddSection = ({ isAdd, data, setSelectedData }) => {
  const openSection = useSelector((state) => state.model.isOpen29);
  const isLoading = useSelector((state) => state.adminSections.isLoading);
  const groups = useSelector((state) => state.groups.groups);
  const groupsOptions = groups?.map((g) => ({ value: g.id, label: g.name }));
  const [fileLabel, setFileLabel] = useState("");

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAllGroupsRequest());
  }, [dispatch]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: data?.name || "",
      image: data?.image || "",
    },
    shouldUnregister: true,
  });

  const groupsDefault =
    data?.groups?.map((g) => ({
      label: g?.name,
      value: g?.id,
    })) ?? undefined;

  const header = (
    <Box>
      <Heading
        fontSize="28px"
        fontWeight="800"
        color={brandPalette.primary}
        fontFamily="'Amiri','Tajawal',sans-serif"
        textAlign="center"
      >
        {isAdd ? "Add course" : "Edit course"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="rgba(69, 11, 17, 0.6)"
        textTransform="uppercase"
        letterSpacing="0.12em"
        fontWeight="700"
        textAlign="center"
      >
        Course name and linked groups
      </Text>
    </Box>
  );

  const baseUrl = 'https://api.edutopiaonline.com/public/';

  const onSubmit = async (values) => {
    const action = () => {
      dispatch(closeAddSectionkModel());
      setSelectedData({});
      reset();
    };

    if (isAdd) {
      const formData = new FormData();
      formData.append("name", values?.name);
      if (values?.student_group_ids?.length) {
        values.student_group_ids.forEach((id, idx) => {
          formData.append(`student_group_ids[${idx}]`, id);
        });
      }
      if (values?.image) {
        formData.append("image", values?.image);
      }
      await dispatch(createAdminSectionRequest(formData, action));
    } else {
      const formData = new FormData();
      formData.append("_method", "PUT");
      formData.append("name", values?.name ?? data?.name);
      if (values?.student_group_ids?.length) {
        values.student_group_ids.forEach((id, idx) => {
          formData.append(`student_group_ids[${idx}]`, id);
        });
      }
      if (values?.image) {
        formData.append("image", values?.image);
      }
      dispatch(
        editAdminSectionRequest({
          id: data?.id,
          formData,
          action,
        })
      );
    }
  };

  const closeFunc = () => {
    dispatch(closeAddSectionkModel());
    setSelectedData({});
    reset();
  };

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openSection}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="lg"
      hideDefaultFooter
      contentProps={{
        dir: "ltr",
        bg: brandPalette.warmBeige,
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "2xl",
      }}
      headerProps={{
        px: 8,
        pt: 8,
        pb: 6,
        borderBottom: "1px solid rgba(216, 206, 195, 0.5)",
      }}
      bodyProps={{ px: 8, py: 8 }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <SimpleGrid columns={{ base: 1, md: 1 }} spacingY={6}>
            <MainInput
              label="Course name"
              defaultValues={data?.name}
              error={errors?.name?.message}
              placeholder="Enter course name"
              register={register}
              name="name"
            />

            <FormControl>
              <VStack align="stretch" spacing={2}>
                <FormLabel m={0} fontSize="14px" fontWeight="700" color={brandPalette.primary} textAlign="left">
                  Course image
                </FormLabel>
                <Button
                  bg={brandPalette.primary}
                  color="white"
                  borderRadius="lg"
                  position="relative"
                  h="42px"
                  fontWeight="700"
                  _hover={{ bg: brandPalette.accentDark }}
                >
                  <Text>{fileLabel || "Choose file to upload"}</Text>
                  <Input
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setValue("image", f);
                        setFileLabel(f.name);
                      }
                    }}
                    type="file"
                    accept="image/*"
                    opacity="0"
                    position="absolute"
                    w="100%"
                    h="100%"
                    left={0}
                    top={0}
                    cursor="pointer"
                  />
                </Button>
              </VStack>
            </FormControl>

            {data?.image && (
              <Image src={baseUrl + data?.image} alt="Course image" />
            )}

            <FormControl>
              <VStack align="stretch" spacing={2}>
                <FormLabel m={0} fontSize="14px" fontWeight="700" color={brandPalette.primary} textAlign="left">
                  Groups
                </FormLabel>
                <Select
                  inputId="section_groups"
                  isMulti
                  options={groupsOptions}
                  defaultValue={groupsDefault}
                  onChange={(opts) => {
                    const ids = (opts || []).map((o) => o.value);
                    setValue("student_group_ids", ids, { shouldDirty: true });
                  }}
                  styles={colourStyles}
                  placeholder="Select groups"
                />
              </VStack>
            </FormControl>
          </SimpleGrid>

          <Flex justify="flex-end" gap={6} pt={2}>
            <Button
              variant="ghost"
              color={brandPalette.accentDark}
              fontWeight="800"
              _hover={{ textDecoration: "underline" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddSection;
